import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Calendar, MapPin, Users, DollarSign, MessageSquare, CheckCircle, XCircle, AlertTriangle, AlertCircle, Clock, RefreshCw, ChevronDown, ChevronUp, Tag, FileText } from 'lucide-react';
import { formatShortDate } from '../../utils/dateFormatter';
import { getPaymentStatus, getPaymentBorderClass } from '../../utils/paymentStatus';
import { SendReminderButton } from '../SendReminderButton';
import ProposalViewDialog from './ProposalViewDialog';
import axios from 'axios';
import { toast } from 'sonner';

const VendorInquiryCard = ({
  inquiry,
  bookings,
  getStatusBadge,
  getBookingForInquiry,
  handleOpenMessaging,
  setProgressNoteInquiry,
  setOnHoldInquiry,
  setDecliningInquiry,
  markCompleted,
  openStartWorkDialog,
  setShowServiceAgreement,
  setSelectedBooking,
  onBookingCancelled,
  onBookingUpdated,
  viewMode = 'detailed',
  highlight = false,
}) => {
  const booking = getBookingForInquiry(inquiry.id);
  const isCompactView = viewMode === 'compact';
  const [expanded, setExpanded] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [extensionHours, setExtensionHours] = useState(24);
  const [extending, setExtending] = useState(false);
  const [showExpiredCancelConfirm, setShowExpiredCancelConfirm] = useState(false);
  const [showExtendFinalDialog, setShowExtendFinalDialog] = useState(false);
  const [finalExtensionHours, setFinalExtensionHours] = useState(24);
  const [extendingFinal, setExtendingFinal] = useState(false);
  const [showProposalDialog, setShowProposalDialog] = useState(false);

  // Scroll the card into view + flash a soft ring when the Schedule tap
  // handler signals this inquiry as the highlight target. Reset is owned by
  // the parent (so back-tap behavior stays predictable).
  const cardRef = React.useRef(null);
  React.useEffect(() => {
    if (highlight && cardRef.current) {
      try { cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch { /* no-op */ }
    }
  }, [highlight]);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // ── Derived State ──
  const isPaymentWindowExpired = () => {
    if (!booking || booking.deposit_paid) return false;
    return booking.payment_window_status === 'expired' || booking.can_pay_deposit === false;
  };
  const paymentWindowExpired = isPaymentWindowExpired();

  const isEventPassed = () => {
    if (!inquiry.event_date) return false;
    return new Date() > new Date(inquiry.event_date);
  };

  const daysSinceEvent = () => {
    if (!inquiry.event_date) return 0;
    return Math.floor((new Date() - new Date(inquiry.event_date)) / (1000 * 60 * 60 * 24));
  };

  const showCloseOutPrompt = () => {
    if (!booking) return false;
    // Prefer the backend-computed lifecycle.closeout signal (Booking Lifecycle
    // Fix, 2026-02-29). It's authoritative because the backend now also
    // bypasses the 72h cancel guard for these rows, so they will succeed.
    // Falls back to inline derivation for older API payloads that pre-date
    // the new key (defensive during rollout — explicit user choice §4a).
    if (booking.lifecycle?.closeout?.state === 'required') return true;

    // ── Legacy inline derivation (kept as fallback) ──
    // `confirmed` covers deposit-paid bookings whose backend status is still
    // 'confirmed' (older flow) — without it the close-out CTA never appears
    // for those rows even after the event date has passed, leaving them
    // stuck under Ready to Close with no action.
    if (!['pending', 'confirmed', 'deposit_paid', 'in_progress'].includes(booking.status) && booking.status !== 'completed_by_vendor') return false;
    return isEventPassed() && booking.status !== 'completed' && booking.status !== 'completed_by_vendor';
  };

  const isWithin72Hours = () => {
    if (paymentWindowExpired) return false;
    if (!inquiry.event_date) return false;
    const hours = (new Date(inquiry.event_date) - new Date()) / (1000 * 60 * 60);
    return hours < 72 && hours > 0;
  };

  const hoursUntilEvent = () => {
    if (!inquiry.event_date) return null;
    return Math.floor((new Date(inquiry.event_date) - new Date()) / (1000 * 60 * 60));
  };

  const needsResponseNudge = (() => {
    if (!['pending', 'reviewing'].includes(inquiry.status)) return false;
    return (new Date() - new Date(inquiry.created_at)) / (1000 * 60 * 60) > 24;
  })();

  const canExtendWindow = () => {
    if (!booking || booking.deposit_paid) return false;
    if (booking.payment_window_extended_count >= 3) return false;
    // Ready To Close CTA Past-Event Fix (2026-06-01): extending a payment
    // window for an event already in the past is meaningless — the
    // customer cannot deliver a service that already happened. Without
    // this guard, the CTA cascade in getPrimaryCTA hits the Extend branch
    // (line 238) before the unpaid-closeout branch (line 258), trapping
    // past-event unpaid rows under an Extend button that solves nothing.
    if (isEventPassed()) return false;
    return booking.can_extend !== false;
  };

  const canExtendFinalPayment = () => {
    if (!booking || !booking.deposit_paid || booking.final_paid) return false;
    const fpExtCount = booking.final_payment_extended_count || 0;
    if (fpExtCount >= 3) return false;
    // Same reasoning as canExtendWindow: no point extending a final-payment
    // deadline when the event itself is in the past.
    if (isEventPassed()) return false;
    return booking.lifecycle?.final_payment?.can_extend !== false;
  };

  const canVendorCancel = booking &&
    !['completed', 'completed_by_vendor', 'cancelled', 'cancelled_by_customer', 'cancelled_by_vendor'].includes(booking.status) &&
    !isWithin72Hours();

  const paymentStatus = getPaymentStatus(inquiry, booking);
  const paymentBorder = getPaymentBorderClass(paymentStatus);
  const borderClass = paymentBorder || (needsResponseNudge ? 'border-l-[3px] border-l-amber-400 border-gray-100' : (showCloseOutPrompt() ? 'border-l-[3px] border-l-orange-400 border-gray-100' : 'border-gray-100'));
  // Visual polish (Feb 2026): default cards lift off the page with the
  // shared `shadow-tile` token (matches Customer Events + Dashboard
  // tiles). High-urgency cards keep their red-tinted urgency shadow so
  // the alert still reads above the resting elevation. No vertical
  // height change — shadow is offset/blur only.
  const shadowClass = paymentStatus.urgency === 'high'
    ? 'shadow-[0_2px_8px_-2px_rgba(239,68,68,0.15)]'
    : 'shadow-tile hover:shadow-card';

  // ── Handlers ──
  const handleCancelBooking = async () => {
    if (!cancelReason.trim()) { toast.error('Please provide a reason'); return; }
    setCancelling(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/bookings/${booking.id}/cancel`, { reason: cancelReason }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Booking cancelled');
      setShowCancelDialog(false);
      setCancelReason('');
      if (onBookingCancelled) onBookingCancelled(response.data.booking);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    } finally { setCancelling(false); }
  };

  const handleExtendDepositWindow = async () => {
    setExtending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/bookings/${booking.id}/extend-deposit-window`, { extension_hours: extensionHours }, { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 });
      toast.success(`Deposit window extended by ${extensionHours} hours`);
      setShowExtendDialog(false);
      if (onBookingUpdated) onBookingUpdated(response.data.booking);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extend');
    } finally { setExtending(false); }
  };

  const handleExtendFinalPaymentWindow = async () => {
    setExtendingFinal(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/bookings/${booking.id}/extend-final-payment-window`, { extension_hours: finalExtensionHours }, { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 });
      toast.success(`Final payment window extended by ${finalExtensionHours} hours`);
      setShowExtendFinalDialog(false);
      if (onBookingUpdated) onBookingUpdated(response.data.booking);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extend');
    } finally { setExtendingFinal(false); }
  };

  // ── PRIMARY ALERT (single, highest priority) ──
  const getPrimaryAlert = () => {
    // 1. Payment overdue / expired (highest)
    if (paymentStatus.status === 'final_overdue') {
      const dueDate = booking?.final_payment_due_date ? new Date(booking.final_payment_due_date) : null;
      const days = dueDate ? Math.floor((new Date() - dueDate) / (1000 * 60 * 60 * 24)) : 0;
      return { type: 'error', text: `Final payment ${days}d overdue — do not proceed without payment` };
    }
    if (paymentStatus.status === 'deposit_expired') {
      return { type: 'muted', text: 'Deposit window expired — booking not secured' };
    }
    if (paymentStatus.status === 'final_due_soon') {
      return { type: 'warning', text: 'Final payment required before event — do not proceed without payment' };
    }
    if (paymentStatus.status === 'deposit_required') {
      return { type: 'info', text: `Awaiting deposit${booking?.deposit_due_date ? ` (due ${new Date(booking.deposit_due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})` : ''}` };
    }
    // 2. Ready to close
    if (showCloseOutPrompt()) {
      return { type: 'orange', text: `Event passed ${daysSinceEvent()}d ago — close out when ready` };
    }
    // 3. Awaiting response
    if (needsResponseNudge) {
      return { type: 'warning', text: 'Awaiting your response' };
    }
    // 4. Booked / on track
    if (booking && booking.deposit_paid && !booking.final_paid && !isEventPassed()) {
      const dueDate = booking.final_payment_due_date ? new Date(booking.final_payment_due_date) : null;
      if (dueDate) return { type: 'success', text: `On track — Final due ${dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` };
      return { type: 'success', text: 'Booking confirmed' };
    }
    if (booking && booking.deposit_paid && booking.final_paid && booking.status !== 'completed') {
      return { type: 'success', text: 'Paid in full' };
    }
    return null;
  };

  const primaryAlert = getPrimaryAlert();

  // ── PRIMARY CTA (solid green button — never "Message Customer") ──
  const getPrimaryCTA = () => {
    // Booking Closeout UI Regression Fix (2026-05-30): cancelled/completed
    // bookings must NEVER surface actionable CTAs. The card already renders
    // a passive "Request closed" caption (line ~611). Earlier the cascade
    // below could still produce Extend / Cancel & Close on cancelled rows
    // because `payment_window_status` flips to 'expired' for terminal
    // bookings (see services/payment_service.py line 169), which then hit
    // the unpaid-window branches without a status check. Chat icon remains
    // available (handled separately via `showChatIcon`) so the vendor can
    // still message about refunds / context post-cancel.
    const TERMINAL_BOOKING_STATUSES = ['cancelled', 'cancelled_by_customer', 'cancelled_by_vendor', 'completed', 'completed_by_vendor'];
    if (booking && TERMINAL_BOOKING_STATUSES.includes(booking.status)) return null;

    if (['pending', 'reviewing'].includes(inquiry.status)) {
      return { label: 'Respond', icon: <MessageSquare className="h-3.5 w-3.5" />, action: () => handleOpenMessaging(inquiry), variant: 'primary' };
    }
    if (paymentStatus.status === 'final_overdue' && canExtendFinalPayment()) {
      return { label: 'Extend', icon: <RefreshCw className="h-3.5 w-3.5" />, action: () => setShowExtendFinalDialog(true), variant: 'primary' };
    }
    if (paymentWindowExpired && !booking?.deposit_paid && canExtendWindow()) {
      return { label: 'Extend', icon: <RefreshCw className="h-3.5 w-3.5" />, action: () => setShowExtendDialog(true), variant: 'primary' };
    }
    // Phase-1 fix: when the deposit window is expired and the vendor has
    // used all 3 extensions, the card previously rendered no primary CTA —
    // leaving the vendor on a dead-end "Deposit Window Expired" banner with
    // only a tiny tertiary link buried inside the Details expansion.
    // Promote "Cancel & Close" to the primary slot here so the next action
    // is one tap from the surface. No business-rule change: this only
    // exposes the same close path that already lived inside expanded view.
    if (paymentWindowExpired && !booking?.deposit_paid && !canExtendWindow()) {
      return { label: 'Cancel & Close', icon: <XCircle className="h-3.5 w-3.5" />, action: () => setShowExpiredCancelConfirm(true), variant: 'primary' };
    }
    // Booking Lifecycle Fix (2026-02-29): when the event passed and the
    // deposit was never paid, "Mark Complete" cannot succeed (backend
    // rejects with "Deposit must be paid first"). The new unpaid-closeout
    // bypass on /api/bookings/{id}/cancel is the correct action — surface
    // Cancel & Close here so the vendor isn't sent into a 400.
    const closeoutReason = booking?.lifecycle?.closeout?.reason;
    const isUnpaidCloseout = closeoutReason === 'deposit_window_expired_unpaid' || closeoutReason === 'event_passed_never_paid';
    if (showCloseOutPrompt() && isUnpaidCloseout) {
      return { label: 'Cancel & Close', icon: <XCircle className="h-3.5 w-3.5" />, action: () => setShowExpiredCancelConfirm(true), variant: 'primary' };
    }
    if (showCloseOutPrompt()) {
      return { label: 'Mark Complete', icon: <CheckCircle className="h-3.5 w-3.5" />, action: () => markCompleted(inquiry.id), variant: 'orange' };
    }
    // Mark Complete must only surface once the event is actually ready to
    // close (event date today or past). Previously a paid-in-full future
    // booking surfaced this CTA inside the Booked filter, letting a vendor
    // mark an event complete weeks before it happened. The Booked tab
    // should focus on payment status; close-out belongs in Ready to Close.
    if (booking?.deposit_paid && booking?.final_paid && !['completed', 'completed_by_vendor'].includes(booking?.status) && isEventPassed()) {
      return { label: 'Mark Complete', icon: <CheckCircle className="h-3.5 w-3.5" />, action: () => markCompleted(inquiry.id), variant: 'primary' };
    }
    if (booking?.service_agreement_accepted_by_customer && !booking?.service_agreement_accepted_by_vendor) {
      return { label: 'Sign Agreement', icon: <CheckCircle className="h-3.5 w-3.5" />, action: () => { setSelectedBooking(booking); setShowServiceAgreement(true); }, variant: 'primary' };
    }
    if (inquiry.status === 'proposal_rejected') {
      return { label: 'Revise Proposal', icon: <MessageSquare className="h-3.5 w-3.5" />, action: () => handleOpenMessaging(inquiry), variant: 'primary' };
    }
    // No primary button for other states — chat icon handles messaging
    return null;
  };

  // ── CHAT ICON — replaces all "Message Customer" text/buttons with a compact 40px icon ──
  const showChatIcon = !['declined', 'cancelled'].includes(inquiry.status)
    && !['pending', 'reviewing'].includes(inquiry.status);

  // ── View Proposal availability ──
  // Phase-1: lightweight read-only access to the proposal the vendor sent.
  // Available once the inquiry has reached a state where a proposal exists.
  // No new system; just a button that opens a dialog fed by the existing
  // GET /api/inquiries/{id}/proposal endpoint.
  const hasViewableProposal = [
    'proposal_sent',
    'proposal_rejected',
    'accepted',
    'agreement_signed',
    'in_progress',
    'on_hold',
    'completed_by_vendor',
    'completed',
  ].includes(inquiry.status);

  // ── SECONDARY CTA (text-only, never messaging) ──
  const getSecondaryCTA = () => {
    if (['pending', 'reviewing'].includes(inquiry.status)) {
      return { label: "Can't help", action: () => setDecliningInquiry(inquiry) };
    }
    if (inquiry.status === 'in_progress') {
      return { label: 'Add Note', action: () => setProgressNoteInquiry(inquiry) };
    }
    if (inquiry.status === 'on_hold') {
      return { label: 'Resume', action: () => openStartWorkDialog(inquiry) };
    }
    return null;
  };

  const primaryCTA = getPrimaryCTA();
  const secondaryCTA = getSecondaryCTA();

  // ── Alert style map ──
  const alertStyles = {
    error: 'bg-red-50 border-red-200 text-red-700',
    warning: 'bg-amber-50 border-amber-200 text-amber-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    info: 'bg-blue-50 border-blue-200 text-blue-700',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    muted: 'bg-gray-50 border-gray-200 text-gray-600',
  };

  const alertIcons = {
    error: <AlertTriangle className="h-3.5 w-3.5 shrink-0" />,
    warning: <AlertCircle className="h-3.5 w-3.5 shrink-0" />,
    orange: <Clock className="h-3.5 w-3.5 shrink-0" />,
    info: <Clock className="h-3.5 w-3.5 shrink-0" />,
    success: <CheckCircle className="h-3.5 w-3.5 shrink-0" />,
    muted: <AlertCircle className="h-3.5 w-3.5 shrink-0" />,
  };

  // ── Compact view: chat-like row. Preserves reachability — tapping the row
  // opens the messaging thread (same modal as Simple/Detailed), and the
  // "Open full request" button switches the card to the detailed view so
  // every workflow action remains one click away. No dead ends.
  if (isCompactView) {
    const unread = Number(inquiry.unread_count || 0);
    const activityTs = inquiry.last_message_at || inquiry.updated_at || inquiry.created_at;
    const relTime = (() => {
      if (!activityTs) return '';
      const d = new Date(activityTs);
      const now = new Date();
      const diffMs = now - d;
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) return 'just now';
      if (diffMin < 60) return `${diffMin}m ago`;
      const diffH = Math.floor(diffMin / 60);
      if (diffH < 24) return `${diffH}h ago`;
      const diffD = Math.floor(diffH / 24);
      if (diffD < 7) return `${diffD}d ago`;
      return formatShortDate(d);
    })();
    const preview = inquiry.last_message_preview || inquiry.message || '';

    return (
      <div
        key={inquiry.id}
        className={`rounded-xl border bg-white shadow-tile hover:shadow-card flex items-center gap-3 px-3.5 py-3 cursor-pointer hover:bg-gray-50 transition-all ${unread > 0 ? 'border-emerald-200 bg-emerald-50/30' : 'border-gray-200'}`}
        onClick={() => handleOpenMessaging(inquiry)}
        data-testid={`inquiry-compact-${inquiry.id}`}
      >
        {/* Unread dot */}
        <div className="w-2 h-2 rounded-full shrink-0 mt-1 self-start" data-testid={`unread-dot-${inquiry.id}`}>
          {unread > 0 && <span className="block w-2 h-2 rounded-full bg-emerald-500" />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className={`text-sm truncate ${unread > 0 ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
              {inquiry.customer_name}
            </h3>
            <div className="shrink-0">{getStatusBadge(inquiry.status, inquiry.vendor_accepted, inquiry.customer_accepted)}</div>
          </div>
          <p className="text-[11px] text-gray-500 truncate mb-0.5">
            <span className="inline-flex items-center gap-1"><Calendar className="h-2.5 w-2.5" />{inquiry.event_type} &middot; {formatShortDate(inquiry.event_date)}</span>
          </p>
          {preview && (
            <p className="text-[11px] text-gray-500 truncate">{preview}</p>
          )}
        </div>

        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className="text-[10px] text-gray-400 whitespace-nowrap" data-testid={`compact-timestamp-${inquiry.id}`}>{relTime}</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleOpenMessaging(inquiry);
            }}
            className="text-[10px] text-emerald-700 hover:text-emerald-900 underline underline-offset-2"
            data-testid={`compact-open-full-${inquiry.id}`}
            title="Open conversation and full request"
          >
            Open
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      key={inquiry.id}
      ref={cardRef}
      className={`rounded-xl border bg-white overflow-hidden ${borderClass} ${shadowClass} ${highlight ? 'ring-2 ring-emerald-400 ring-offset-2 transition-shadow' : ''}`}
      data-testid={`inquiry-${inquiry.id}`}
    >
      <div className="py-3 px-4">
        {/* ── Row 1: Name + Status Badge ── */}
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <h3 className="font-semibold text-sm truncate" data-testid="inquiry-customer-name">{inquiry.customer_name}</h3>
          <div className="shrink-0">{getStatusBadge(inquiry.status, inquiry.vendor_accepted, inquiry.customer_accepted)}</div>
        </div>

        {/* ── Row 2: Compact metadata line ── */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500 mb-2">
          <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{inquiry.event_type} &middot; {formatShortDate(inquiry.event_date)}</span>
          <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{inquiry.location}</span>
          {inquiry.guest_count && <span className="flex items-center gap-1"><Users className="h-3 w-3" />{inquiry.guest_count}</span>}
          {inquiry.budget_range && <span className="flex items-center gap-1"><DollarSign className="h-3 w-3" />{inquiry.budget_range}</span>}
        </div>

        {/* ── Selected package/service(s) ── */}
        {inquiry.selected_package && (
          <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid="inquiry-selected-package">
            {inquiry.selected_package.split(', ').map((name, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 rounded-md px-2 py-1">
                <Tag className="h-3 w-3" />
                <span className="font-medium">{name}</span>
              </span>
            ))}
          </div>
        )}

        {/* ── Row 3: Message preview (truncated) ── */}
        {inquiry.message && (
          <p className="text-xs text-gray-500 line-clamp-2 mb-2" data-testid="inquiry-message">{inquiry.message}</p>
        )}

        {/* ── Row 4: Payment summary (if booking exists, not expired, compact) ── */}
        {booking && !paymentWindowExpired && !['declined', 'cancelled'].includes(inquiry.status) && (
          <div className="flex items-center gap-2 text-xs mb-2">
            {booking.deposit_paid && booking.final_paid ? (
              <span className="text-emerald-600 font-medium">Paid in full &middot; ${inquiry.agreed_price}</span>
            ) : inquiry.agreed_price ? (
              <span className="text-gray-600">
                ${inquiry.agreed_price} &middot;
                <span className={booking.deposit_paid ? ' text-emerald-600' : ' text-gray-400'}> Deposit {booking.deposit_paid ? '✓' : 'pending'}</span>
                <span className={booking.final_paid ? ' text-emerald-600' : ' text-gray-400'}> &middot; Final {booking.final_paid ? '✓' : booking.deposit_paid ? 'due' : '—'}</span>
              </span>
            ) : null}
          </div>
        )}

        {/* ── Row 5: PRIMARY ALERT (single, highest-priority) ── */}
        {primaryAlert && (
          <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md border text-xs font-medium mb-2 ${alertStyles[primaryAlert.type]}`} data-testid="primary-alert">
            {alertIcons[primaryAlert.type]}
            <span>{primaryAlert.text}</span>
          </div>
        )}

        {/* ── Row 6: CTAs — mobile-first. Wraps onto a second row when the
            combined buttons exceed the card width so the action area never
            forces the page to scroll horizontally. The card root also has
            `overflow-hidden` as a safety net. ── */}
        <div className="flex flex-wrap items-center gap-2 min-w-0" data-testid="card-action-row">
          {primaryCTA && (
            <Button
              onClick={primaryCTA.action}
              size="sm"
              className={`text-xs h-8 whitespace-nowrap ${
                primaryCTA.variant === 'primary' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' :
                primaryCTA.variant === 'orange' ? 'bg-orange-600 hover:bg-orange-700 text-white' :
                'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
              data-testid="primary-cta"
            >
              {primaryCTA.icon}
              <span className="ml-1.5">{primaryCTA.label}</span>
            </Button>
          )}

          {/* Message pill — soft gray, icon + label, matched height */}
          {showChatIcon && (
            <button
              type="button"
              onClick={() => handleOpenMessaging(inquiry)}
              className="shrink-0 min-h-[40px] sm:min-h-0 sm:h-8 px-3 rounded-md bg-gray-100 hover:bg-gray-200 flex items-center gap-1.5 transition-colors whitespace-nowrap"
              data-testid="chat-icon-btn"
            >
              <MessageSquare className="h-3.5 w-3.5 text-gray-500" />
              <span className="text-xs font-medium text-gray-600">{primaryCTA ? 'Message' : 'Message Customer'}</span>
            </button>
          )}

          {secondaryCTA && (
            <button
              type="button"
              onClick={secondaryCTA.action}
              className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1.5 whitespace-nowrap shrink-0"
              data-testid="secondary-cta"
            >
              {secondaryCTA.label}
            </button>
          )}

          {/* Phase-1: lightweight read-only proposal access. Vendors used to
              have no way to review the proposal they had submitted; the
              backend endpoint already supported it but no UI consumed it. */}
          {hasViewableProposal && (
            <button
              type="button"
              onClick={() => setShowProposalDialog(true)}
              className="shrink-0 min-h-[40px] sm:min-h-0 sm:h-8 px-3 rounded-md bg-gray-50 hover:bg-gray-100 border border-gray-200 flex items-center gap-1.5 transition-colors whitespace-nowrap"
              data-testid="view-proposal-btn"
            >
              <FileText className="h-3.5 w-3.5 text-gray-500" />
              <span className="text-xs font-medium text-gray-600">View Proposal</span>
            </button>
          )}

          {/* Reminder — soft gray pill, matched height */}
          {booking && !paymentWindowExpired && !booking.deposit_paid && booking.service_agreement_accepted_by_vendor && booking.service_agreement_accepted_by_customer && !['cancelled', 'completed', 'declined'].includes(inquiry.status) && booking.id && (
            <SendReminderButton
              entityType="booking"
              entityId={booking.id}
              nudgeState="deposit_required"
              label="Remind"
              size="sm"
            />
          )}
          {booking && booking.deposit_paid && !booking.final_paid && !['cancelled', 'completed', 'declined'].includes(inquiry.status) && !['cancelled', 'completed', 'completed_by_vendor'].includes(booking.status) && booking.id && (
            <SendReminderButton
              entityType="booking"
              entityId={booking.id}
              nudgeState="final_payment_required"
              label="Remind"
              size="sm"
            />
          )}

          {/* Details toggle — tertiary text, always rightmost */}
          {!['declined', 'cancelled', 'completed'].includes(inquiry.status) && (booking || inquiry.vendor_acceptance_note || inquiry.customer_confirmation_note) && (
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="ml-auto text-xs text-gray-400 hover:text-gray-600 flex items-center gap-0.5 whitespace-nowrap shrink-0"
              data-testid="toggle-details"
            >
              {expanded ? 'Less' : 'Details'}
              {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
          )}
        </div>

        {/* ── EXPANDED DETAILS ── */}
        {expanded && (
          <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
            {/* Received date */}
            <p className="text-[11px] text-gray-400">Received: {formatShortDate(inquiry.created_at)}</p>

            {/* Full payment breakdown */}
            {booking && !paymentWindowExpired && (
              <div className="p-2 bg-emerald-50 border border-emerald-100 rounded text-xs">
                <div className="flex justify-between text-emerald-800 font-medium mb-1">
                  <span>{booking.deposit_paid && booking.final_paid ? 'Paid in Full' : 'Proposal Accepted'}</span>
                  <span>${inquiry.agreed_price}</span>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px]">
                  <span className={booking.service_agreement_accepted_by_vendor ? 'text-gray-400' : 'text-gray-600'}>Agreement {booking.service_agreement_accepted_by_vendor ? '✓' : 'pending'}</span>
                  <span className={booking.deposit_paid ? 'text-gray-400' : 'text-emerald-700 font-medium'}>Deposit ${booking.deposit_amount?.toFixed(2)} {booking.deposit_paid ? '✓' : 'pending'}</span>
                  <span className={booking.final_paid ? 'text-gray-400' : 'text-gray-600'}>Final ${booking.final_amount?.toFixed(2)} {booking.final_paid ? '✓' : booking.deposit_paid ? 'due' : '—'}</span>
                </div>
              </div>
            )}

            {/* Expired deposit detail */}
            {paymentWindowExpired && !booking?.deposit_paid && (
              <div className="p-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600">
                <p className="font-medium text-gray-700 mb-0.5">Deposit Window Expired</p>
                <p>The deposit wasn&apos;t received in time.</p>
                {booking?.payment_window_extended_count > 0 && (
                  <p className="text-[11px] text-gray-400 mt-1">Extended {booking.payment_window_extended_count}/3 times</p>
                )}
              </div>
            )}

            {/* Vendor terms */}
            {inquiry.vendor_acceptance_note && (
              <div className="p-2 bg-green-50 border border-green-100 rounded text-xs">
                <span className="font-medium text-green-800">Your Terms: ${inquiry.agreed_price}</span>
                <p className="text-gray-600 mt-0.5">{inquiry.vendor_acceptance_note}</p>
              </div>
            )}

            {/* Customer note */}
            {inquiry.customer_confirmation_note && (
              <div className="p-2 bg-blue-50 border border-blue-100 rounded text-xs">
                <span className="font-medium text-blue-800">Customer Note:</span>
                <p className="text-gray-600 mt-0.5">{inquiry.customer_confirmation_note}</p>
              </div>
            )}

            {/* Progress notes */}
            {inquiry.progress_notes && inquiry.progress_notes.length > 0 && (
              <div className="p-2 bg-gray-50 rounded text-[11px]">
                <p className="font-medium mb-0.5">Notes:</p>
                {inquiry.progress_notes.map((note, idx) => (
                  <div key={idx} className="text-gray-600">{formatShortDate(note.timestamp)}: {note.note}</div>
                ))}
              </div>
            )}

            {/* On-hold note */}
            {inquiry.status === 'on_hold' && inquiry.on_hold_note && (
              <p className="text-xs text-yellow-700 bg-yellow-50 p-2 rounded">On Hold: {inquiry.on_hold_note}</p>
            )}

            {/* Cannot cancel warning — suppressed for unpaid-closeout rows where
                the new bypass on /api/bookings/{id}/cancel makes the close
                possible regardless of 72h proximity. */}
            {booking && isWithin72Hours() && !(paymentWindowExpired && !booking?.deposit_paid) && !['completed', 'completed_by_vendor', 'cancelled'].includes(booking.status) && (
              <p className="text-[11px] text-orange-600">Cannot cancel — Event in {hoursUntilEvent()}h</p>
            )}

            {/* Cancel action */}
            {canVendorCancel && !(paymentWindowExpired && !booking?.deposit_paid) && (
              <button onClick={() => setShowCancelDialog(true)} className="text-[11px] text-gray-400 hover:text-red-500">Cancel Booking</button>
            )}
            {paymentWindowExpired && !booking?.deposit_paid && (
              <button onClick={() => setShowExpiredCancelConfirm(true)} className="text-[11px] text-gray-400 hover:text-red-500">Cancel this request</button>
            )}
          </div>
        )}

        {/* Closed inquiry */}
        {['declined', 'cancelled', 'completed'].includes(inquiry.status) && (
          <p className="text-[11px] text-gray-400 mt-1">Request closed</p>
        )}
      </div>

      {/* ── DIALOGS (unchanged logic, same as before) ──
          Booking Closeout UI Regression Fix (2026-05-30) — iOS keyboard fix:
          dialogs were `items-center` centered. iOS keyboard covers fixed/
          centered modals because it does NOT reflow the viewport. We anchor
          modals to the top on small screens with `pt-[15vh]` so the
          keyboard rises into the empty bottom area, and cap inner panel
          height with `max-h-[85vh] overflow-y-auto` so the textarea can
          scroll into view when focused. Behavior unchanged on tablets/web
          (`sm:` breakpoint restores centered layout). */}
      {showCancelDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-[15vh] sm:pt-4">
          <div className="bg-white rounded-lg w-full max-w-md p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center mb-4"><AlertTriangle className="h-6 w-6 text-red-600 mr-2" /><h3 className="text-lg font-bold">Cancel Booking</h3></div>
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg mb-4"><p className="text-sm font-medium text-yellow-800">Customer will receive full refund (${booking?.deposit_amount?.toFixed(2)})</p></div>
            <p className="text-sm text-gray-600 mb-4">Reason for cancellation:</p>
            <textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="e.g., Schedule conflict" className="w-full border border-gray-300 rounded-lg px-3 py-2 h-24 resize-none scroll-mt-24" />
            <div className="flex gap-3 mt-4">
              <Button onClick={() => { setShowCancelDialog(false); setCancelReason(''); }} variant="outline" className="flex-1" disabled={cancelling}>Keep</Button>
              <Button onClick={handleCancelBooking} className="flex-1 bg-red-600 hover:bg-red-700" disabled={cancelling || !cancelReason.trim()}>{cancelling ? 'Cancelling...' : 'Confirm'}</Button>
            </div>
          </div>
        </div>
      )}
      {showExtendDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-[15vh] sm:pt-4">
          <div className="bg-white rounded-lg w-full max-w-md p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center mb-4"><RefreshCw className="h-6 w-6 text-blue-600 mr-2" /><h3 className="text-lg font-bold">Extend Deposit Window</h3></div>
            <p className="text-sm text-gray-600 mb-4">Give the customer more time to pay their deposit.</p>
            <div className="space-y-3 mb-4">
              <p className="text-sm font-medium text-gray-700">Extend by:</p>
              <div className="grid grid-cols-3 gap-2">
                {[24, 48, 72].map((h) => (
                  <button key={h} onClick={() => setExtensionHours(h)} className={`py-2 px-3 rounded-lg border text-sm font-medium ${extensionHours === h ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'}`}>{h}h</button>
                ))}
              </div>
              <div className="pt-2">
                <label className="text-xs text-gray-500 block mb-1">Custom hours:</label>
                <input type="number" min="1" max="168" value={extensionHours} onChange={(e) => setExtensionHours(Math.min(168, Math.max(1, parseInt(e.target.value) || 1)))} className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <span className="text-xs text-gray-500 ml-2">hours (max 168)</span>
              </div>
            </div>
            {booking?.payment_window_extended_count > 0 && (<div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4"><p className="text-sm text-amber-800">Extended {booking.payment_window_extended_count} of 3 times</p></div>)}
            <div className="flex gap-3">
              <Button onClick={() => { setShowExtendDialog(false); setExtensionHours(24); }} variant="outline" className="flex-1" disabled={extending}>Cancel</Button>
              <Button onClick={handleExtendDepositWindow} className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={extending}>{extending ? 'Extending...' : `Extend by ${extensionHours}h`}</Button>
            </div>
          </div>
        </div>
      )}
      {showExpiredCancelConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-[15vh] sm:pt-4">
          <div className="bg-white rounded-xl w-full max-w-sm p-6 shadow-xl max-h-[85vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Cancel this request?</h3>
            <p className="text-sm text-gray-600 mb-6">This will close the request. The customer can send a new one later.</p>
            <div className="flex gap-3">
              <Button onClick={() => setShowExpiredCancelConfirm(false)} variant="outline" className="flex-1 h-11 rounded-lg">Keep</Button>
              <Button onClick={() => { setShowExpiredCancelConfirm(false); setShowCancelDialog(true); }} className="flex-1 h-11 rounded-lg bg-red-500 hover:bg-red-600 text-white">Cancel Request</Button>
            </div>
          </div>
        </div>
      )}
      {showExtendFinalDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-[15vh] sm:pt-4">
          <div className="bg-white rounded-lg w-full max-w-md p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center mb-4"><RefreshCw className="h-6 w-6 text-blue-600 mr-2" /><h3 className="text-lg font-bold">Extend Final Payment Window</h3></div>
            <p className="text-sm text-gray-600 mb-4">Give the customer more time to pay the remaining balance.</p>
            <div className="space-y-3 mb-4">
              <p className="text-sm font-medium text-gray-700">Extend by:</p>
              <div className="grid grid-cols-3 gap-2">
                {[24, 48, 72].map((h) => (
                  <button key={h} onClick={() => setFinalExtensionHours(h)} className={`py-2 px-3 rounded-lg border text-sm font-medium ${finalExtensionHours === h ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'}`} data-testid={`final-extend-${h}h`}>{h}h</button>
                ))}
              </div>
              <div className="pt-2">
                <label className="text-xs text-gray-500 block mb-1">Custom hours:</label>
                <input type="number" min="1" max="168" value={finalExtensionHours} onChange={(e) => setFinalExtensionHours(Math.min(168, Math.max(1, parseInt(e.target.value) || 1)))} className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm" data-testid="final-extend-custom-hours" />
                <span className="text-xs text-gray-500 ml-2">hours (max 168)</span>
              </div>
            </div>
            {(booking?.final_payment_extended_count || 0) > 0 && (<div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4"><p className="text-sm text-amber-800">Extended {booking.final_payment_extended_count} of 3 times</p></div>)}
            <div className="flex gap-3">
              <Button onClick={() => { setShowExtendFinalDialog(false); setFinalExtensionHours(24); }} variant="outline" className="flex-1" disabled={extendingFinal} data-testid="final-extend-cancel-btn">Cancel</Button>
              <Button onClick={handleExtendFinalPaymentWindow} className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={extendingFinal} data-testid="final-extend-confirm-btn">{extendingFinal ? 'Extending...' : `Extend by ${finalExtensionHours}h`}</Button>
            </div>
          </div>
        </div>
      )}

      {showProposalDialog && (
        <ProposalViewDialog
          inquiry={inquiry}
          booking={booking}
          open={showProposalDialog}
          onClose={() => setShowProposalDialog(false)}
        />
      )}
    </div>
  );
};

export default VendorInquiryCard;
